import { Badge, Card, Dropdown, ExternalLink, Heading, Text } from '@ui/themed';
import { t } from 'i18next';
import { type Env, isShopify } from '../../../context/env-context';
import { findVariantImage, resolveOptionName } from '../../products/product-metadata';
import type { Product, Variant } from '../../products/types/product';
import { I18N_COMPONENTS } from '../constants';

export function VariantSummaryCard({
  product,
  variant,
  selectedVariantSku,
  onVariantSelect,
  env,
}: {
  product: Product;
  variant: Variant;
  selectedVariantSku: string;
  onVariantSelect: (sku: string) => void;
  env: Env;
}) {
  const platform = env.connected_platform;
  const image = findVariantImage(variant, platform);

  const shopifyUrl = isShopify(platform)
    ? `https://${env.store_id}/admin/products/${product.identifiers.platform}/variants/${variant.identifiers.platform}`
    : undefined;

  return (
    <Card>
      <Card.Header headingLevel="h2">{t(`${I18N_COMPONENTS}.variant_summary.title`)}</Card.Header>
      <Card.Body>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {image && (
              <img
                src={image}
                alt={product.title}
                className="size-14 rounded-md border border-gray-200"
              />
            )}
            <div className="flex flex-col gap-1">
              <Heading style="sm" level="h3">
                {product.title}
              </Heading>
              <div className="flex flex-wrap gap-1">
                {product.slug && <Badge variant="default">{product.slug}</Badge>}
                {product.identifiers.platform && (
                  <Badge variant="default">{product.identifiers.platform}</Badge>
                )}
              </div>
              <div className="flex flex-col gap-0.5 text-sm">
                {variant.option_values.map((optionValue) =>
                  Object.entries(resolveOptionName(product, variant, optionValue)).map(
                    ([key, value]) => (
                      <Text key={key} variant="secondary">
                        <span className="font-medium text-spark-text-primary">{key}</span>: {value}
                      </Text>
                    ),
                  ),
                )}
              </div>
            </div>
          </div>

          {shopifyUrl && (
            <ExternalLink link={shopifyUrl} showIcon>
              {t(`${I18N_COMPONENTS}.variant_summary.view_in_shopify`)}
            </ExternalLink>
          )}
        </div>

        <Dropdown
          label={t(`${I18N_COMPONENTS}.variant_summary.variant`)}
          value={selectedVariantSku}
          onChange={onVariantSelect}
          options={product.variants.map((v) => ({
            value: v.identifiers.sku,
            label: `SKU: ${v.identifiers.sku} (${v.identifiers.platform})`,
          }))}
        />
      </Card.Body>
    </Card>
  );
}
